import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';
import { CookieStorage } from '../shared/services/cookie-storage/cookie-storage.service';
import { UserRulesService } from '../shared/services/user-rules.service';
import KJUR from 'jsrsasign';
import { AuthGuardService } from '../shared/services/auth-guard.service';

import { OptionalAuthGuard } from './optional-auth.guard';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(private auth: OidcClientService, private authGuardService: AuthGuardService,
    private userRules: UserRulesService, private storage: CookieStorage,
    private optionalAuthGuard: OptionalAuthGuard) {
}

private referrers = ['https://adminportaldev.upmchp.com/', 'https://adminportaltst.upmchp.com/', 'https://adminportalstg.upmchp.com/', 'https://adminportal.upmchp.com/'];

  canActivate(next: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    /** document.referrer is empty and lost when redirecting to localhost because HTTP, only HTTPS keeps referrer */
    let referrer = document.referrer;
    if (this.referrers.includes(referrer)) {
      return this.optionalAuthGuard.canActivate(next, routerState);
    } else {
      const authSubject = new Subject<boolean>();
      const fragment = next.fragment;
      if(fragment) {
        const separatedFragment = fragment.split('&');
        let idToken = null;
        let memberTokenSegment = '';
        for(let i = 0; i < separatedFragment.length; i++) {
          if(separatedFragment[i].startsWith('id_token')) {
            idToken = separatedFragment[i].split('=')[1];
          } else if(separatedFragment[i].startsWith('member_token')) {
            memberTokenSegment = separatedFragment[i];
          }
        }
        const token = KJUR.jws.JWS.parse(idToken);
        this.authGuardService.setupUserStore(token.payloadObj.iss, token.payloadObj.acr, token.payloadObj.aud);
        this.auth.createSignInFromIdToken(idToken).subscribe(state => {
          const url = `${window.location.href}&state=${state}`.replace(memberTokenSegment, '');
          this.auth.completeSignIn(url).subscribe(user => {
            this.userRules.setIdToken(user.id_token);
            this.userRules.setPrimaryIdFromProfile(user.profile);
            this.userRules.setSecondaryId(next.queryParamMap.get('memberid'));
            this.authGuardService.setupUserStore(token.payloadObj.iss, token.payloadObj.acr, token.payloadObj.aud);
            authSubject.next(true);
          });
        });
      } else {
        this.auth.getUserComputed().subscribe(user => {
          if(user) {
            if(this.authGuardService.shouldRenewToken()) {
              this.auth.renewToken().subscribe(_ => {
                authSubject.next(true);
              }, error => {
                this.storage.setItem('intended-route', routerState.url);
                this.auth.signIn();
                authSubject.next(false);
              });
            } else {
              if(this.auth.isExpired()) {
                this.storage.setItem('intended-route', routerState.url);
                this.auth.signIn();
                authSubject.next(false);
              } else {
                authSubject.next(true);
              }
            }
          } else {
            this.storage.setItem('intended-route', routerState.url);
            this.auth.signIn();
            authSubject.next(false);
          }
        }, error => {
          authSubject.next(false);
        });
      }
      return authSubject;
    }
  }
}
